/*
 * Retained as DOM dependency for Dawn's product-form.js routing.
 *
 * product-form.js:11 resolves this.cart via
 * `document.querySelector('cart-notification') ||
 * document.querySelector('cart-drawer')` at construct-time. Line
 * 64-67 bails ATC to /cart if this.cart is null (this is the
 * D-Q13 failure mode, reverted as f76a5e0). Visual surface
 * suppressed via rs-tokens.css D-Q14 (820958f) — element renders
 * in DOM, customer never sees it.
 *
 * DO NOT delete this file without:
 * 1. Coordinating a Dawn Divergence patch to product-form.js
 *    (and a parallel patch to quick-add.js:15) so the routing
 *    chain doesn't depend on this.cart
 * 2. Running 2-4 weeks of customer ATC monitoring (the D-Q13
 *    failure was silent — server logs showed cart-add success,
 *    only customer behavior signaled the bug)
 *
 * Dawn Divergence patch at renderContents (commit 9bd373c) is
 * documented separately in CLAUDE.md Dawn Divergences Register.
 * That patch is unrelated to this file's retention rationale.
 *
 * See CLAUDE.md "Surface retirement vs visual suppression"
 * lesson + Session F "cart-notification cleanup" entry for full
 * rationale.
 */
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    // RS (Digital #97): no cart:view here. This panel is hidden (D-Q14); the
    // visible cart dialog is the rs-drawer, and rsOpenDrawer (cart-popup.js)
    // sends cart:view from the cart it already fetched. Calling
    // dispatchCartViewEvent() here added one /cart.json read per add.
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      /* Patch: <cart-notification> renders when settings.cart_type='notification'
         but this theme uses cart-popup; section targets (e.g. cart-notification-product)
         are absent. Null-guard prevents throw in cart-update subscriber. Surfaced
         2026-05-11 after pubsub chain unmasked it. */
      const el = document.getElementById(section.id);
      if (!el) return;
      el.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
